import { Vector2, Vector3, Plane, BoxGeometry, MeshBasicMaterial, Mesh } from 'three';
import { CSS3DObject } from 'three/examples/jsm/renderers/CSS3DRenderer.js';
import { ReactiveComponent } from '../../core/ReactiveComponent.core.js';
import { computeProjectiveMatrix3d } from '../../plugins/3d-2d-transform/src/projectiveMatrix.js';
import { vapiPerspectiveCornerHandleStyle } from '../../constants/vapiPerspectiveHandles.js';
import { ComponentRegistry } from '../../constants/componentRegistry.js';
import { normalizeTimePreset, parseTimePresetSeconds, formatCountdown } from './VRTimerElement.js';

const DEFAULT_WIDTH = 280;
const DEFAULT_HEIGHT = 72;

function escapeHtml(str) {
    return String(str || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

/**
 * VRInputFieldGuess - CSS3D quiz trigger with perspective warping (like VRIframeElement / VRTimerElement).
 */
class VRInputFieldGuess extends ReactiveComponent {
    constructor(params = {}) {
        super(null, {
            mode: params.editMode ? 'edit' : 'view',
        });

        const { model, camera, controls, raycaster, position, id, dbConfig, sizes } = params;
        this.id = id;
        this.model = model;
        this.camera = camera;
        this.controls = controls || null;
        this.position = position || { x: 0, y: 0, z: -540 };
        this.dbConfig = dbConfig || {};
        this._raycaster = raycaster || null;
        this._sizes = sizes || { width: window.innerWidth, height: window.innerHeight };
        this._viewportEl = params.viewportElement || document.querySelector('#webgl') || document.querySelector('#css3d');
        this._ndc = new Vector2();
        this._dragPlane = new Plane();
        this._dragPlaneNormal = new Vector3();
        this._planeIntersectPoint = new Vector3();
        this._dragGrabOffset = new Vector3();
        this._tmpVec = new Vector3();
        this._dragStartDistance = 0;
        this.isDragging = false;
        this._didDragDuringPointer = false;
        this._captureEl = null;
        this._countdownIntervalId = null;
        this._remainingCountdownSeconds = 0;
        this._isEditMode = !!params.editMode;
        this._css3dScale = 1;
        this._userScale = Number(this.dbConfig?.size ?? this.dbConfig?.scale) || 1;
        this.elementClass = null;
        this._cornerHandles = [];
        this._isModalOpen = false;
        this._isSubmitting = false;
        this.parent = this;
        this.name = 'inputFieldGuess';
        this.type = 'inputFieldGuess';
        this.objConfig = {
            isResizable: true,
            isRotatable: true,
        };

        this._buildNode();
        this.anchor.position.set(
            this.position.x,
            this.position.y,
            this.position.z !== undefined ? this.position.z : -540
        );
        this.position = this.anchor.position;
        this.anchor.userData.vrInputFieldGuess = this;

        if (this.state.mode === 'edit') {
            this._attachDragListeners();
        }
        this._applyVisibilityFromConfig();
        this._updatePosterContent();
        return this;
    }

    _getTitle() {
        return this.dbConfig?.title || this.dbConfig?.question || '';
    }

    _getBodyText() {
        return this.dbConfig?.bodyText || '';
    }

    _getCountdownPreset() {
        const raw = String(this.dbConfig?.timePreset || '').trim();
        return raw ? normalizeTimePreset(raw) : '';
    }

    _getCountdownSeconds() {
        const preset = this._getCountdownPreset();
        return preset ? parseTimePresetSeconds(preset) : 0;
    }

    _updatePosterContent() {
        if (this._titleEl) {
            this._titleEl.textContent = this._getTitle() || ((typeof $trans !== 'undefined' && $trans('input-field-guess')) || 'Input guess');
        }
    }

    /** View mode: no HTML, no mesh visuals — actions open/close the modal only. */
    _detachViewVisuals() {
        if (this._isEditMode) return;
        try {
            if (this.css3dObject?.parent) this.css3dObject.parent.remove(this.css3dObject);
        } catch (e) { }
        if (this.dom) {
            this.dom.style.display = 'none';
            this.dom.remove?.();
            if (this.dom.parentNode) this.dom.parentNode.removeChild(this.dom);
        }
        document.querySelectorAll(`[data-id='input_field_guess_${this.id}']`).forEach((node) => node?.remove?.());
        if (this.anchor) this.anchor.visible = false;
        if (this.mesh) this.mesh.visible = false;
    }

    _buildNode() {
        const w = this._domW = Number(this.dbConfig?.width) > 0 ? Number(this.dbConfig.width) : DEFAULT_WIDTH;
        const h = this._domH = Number(this.dbConfig?.height) > 0 ? Number(this.dbConfig.height) : DEFAULT_HEIGHT;
        this._originalPos = [{ x: 0, y: 0 }, { x: w, y: 0 }, { x: w, y: h }, { x: 0, y: h }];

        const dataId = `input_field_guess_${this.id}`;
        Array.from(document.querySelectorAll(`[data-id='${dataId}']`)).forEach((node) => node?.remove?.());

        const root = document.createElement('div');
        root.setAttribute('class', 'vrai-agent-component vr-input-field-guess-wrapper css3d_test_wrapper');
        root.setAttribute('data-css3d-interactive', 'true');
        root.setAttribute(
            'data-css3d-click-target',
            '.vr-input-field-guess-perspective-content, .vr-input-field-guess-corner-handle'
        );
        root.setAttribute('data-id', dataId);
        root.style.cssText = `
            position: relative;
            width: ${w}px;
            height: ${h}px;
            pointer-events: auto;
            will-change: transform;
            user-select: none;
            -webkit-user-select: none;
        `;
        root.classList.add(`vr-input-field-guess-${this.id}`);

        if (this._isEditMode) {
            root.addEventListener('click', (e) => e.stopPropagation());
            root.addEventListener('click', (e) => {
                if (this._didDragDuringPointer) return;
                if (e.target?.closest?.('.vr-input-field-guess-corner-handle')) return;
                this.showEditModal();
            });
        } else {
            root.style.cursor = 'pointer';
        }

        root.addEventListener('pointerdown', (e) => {
            if (e.button !== 0) return;
            if (e.target?.closest?.('.vr-input-field-guess-corner-handle')) return;
            this._didDragDuringPointer = false;
            const hitPerspective = e.target?.closest?.('.vr-input-field-guess-perspective-content')
                ?? document.elementFromPoint(e.clientX, e.clientY)?.closest?.('.vr-input-field-guess-perspective-content');
            if (this.state.mode === 'edit' && hitPerspective && this._onPointerDown) {
                e.preventDefault();
                e.stopPropagation();
                this._onPointerDown(e);
            }
        });

        const saved = this.dbConfig?.perspectiveOffsets;
        this._perspectiveCorners = Array.isArray(saved) && saved.length === 4
            ? saved.map((p) => (p.xr !== undefined
                ? { x: Number(p.xr) * w, y: Number(p.yr) * h }
                : { x: Number(p.x) ?? p.x, y: Number(p.y) ?? p.y }))
            : this._originalPos.map((p) => ({ ...p }));

        const posterContent = document.createElement('div');
        posterContent.className = 'vr-input-field-guess-poster';
        posterContent.innerHTML = `
            <span class="material-icons-outlined vr-input-field-guess-poster__icon" aria-hidden="true">quiz</span>
            <span class="vr-input-field-guess-poster__title"></span>
        `;
        this._titleEl = posterContent.querySelector('.vr-input-field-guess-poster__title');

        const perspectiveContent = document.createElement('div');
        perspectiveContent.className = 'vr-input-field-guess-perspective-content';
        perspectiveContent.style.cssText = 'position:absolute;inset:0;overflow:hidden;transform-origin:0 0;transform-style:flat;will-change:transform;pointer-events:auto;';
        if (this._isEditMode) perspectiveContent.style.cursor = 'grab';
        perspectiveContent.appendChild(posterContent);
        this._perspectiveContent = perspectiveContent;

        const perspectiveWrapper = document.createElement('div');
        perspectiveWrapper.className = 'vr-input-field-guess-perspective-wrapper';
        perspectiveWrapper.style.cssText = 'position:absolute;inset:0;overflow:visible;pointer-events:none;';
        this._perspectiveWrapper = perspectiveWrapper;
        perspectiveWrapper.appendChild(perspectiveContent);

        this._applyPerspectiveTransform = () => {
            const ew = root.offsetWidth || this._domW;
            const eh = root.offsetHeight || this._domH;
            const currentOrigPos = [{ x: 0, y: 0 }, { x: ew, y: 0 }, { x: ew, y: eh }, { x: 0, y: eh }];
            const m = computeProjectiveMatrix3d(currentOrigPos, this._perspectiveCorners);
            perspectiveContent.style.transform = m;
            this._syncHandlePositions?.();
        };
        this._applyPerspectiveTransform();

        if (this.state.mode === 'edit') {
            for (let i = 0; i < 4; i++) {
                const hnd = document.createElement('div');
                hnd.className = 'vr-input-field-guess-corner-handle';
                hnd.dataset.corner = String(i);
                hnd.style.cssText = vapiPerspectiveCornerHandleStyle('pointer-events:auto;');
                const updateHandlePos = () => {
                    hnd.style.left = `${this._perspectiveCorners[i].x}px`;
                    hnd.style.top = `${this._perspectiveCorners[i].y}px`;
                };
                updateHandlePos();
                const onCornerPointerDown = (e) => {
                    if (this.dbConfig?.lockedInEdit) return;
                    e.preventDefault();
                    e.stopPropagation();
                    hnd.style.cursor = 'grabbing';
                    const startX = e.clientX;
                    const startY = e.clientY;
                    const startPos = { x: this._perspectiveCorners[i].x, y: this._perspectiveCorners[i].y };
                    const onMove = (ev) => {
                        const dx = ev.clientX - startX;
                        const dy = ev.clientY - startY;
                        this._perspectiveCorners[i].x = startPos.x + dx;
                        this._perspectiveCorners[i].y = startPos.y + dy;
                        updateHandlePos();
                        this._applyPerspectiveTransform();
                    };
                    const onUp = () => {
                        hnd.style.cursor = 'grab';
                        document.removeEventListener('pointermove', onMove);
                        document.removeEventListener('pointerup', onUp);
                        this.dbConfig = {
                            ...this.dbConfig,
                            perspectiveOffsets: this._perspectiveOffsetsForDb(),
                        };
                        if (this.model?.view?.updateJSON) this.model.view.updateJSON(this.anchor, null);
                    };
                    document.addEventListener('pointermove', onMove);
                    document.addEventListener('pointerup', onUp);
                };
                hnd.addEventListener('pointerdown', onCornerPointerDown);
                perspectiveWrapper.appendChild(hnd);
                this._cornerHandles.push(hnd);
            }
        }

        this._syncHandlePositions = () => {
            this._cornerHandles.forEach((hnd, i) => {
                if (this._perspectiveCorners[i]) {
                    hnd.style.left = `${this._perspectiveCorners[i].x}px`;
                    hnd.style.top = `${this._perspectiveCorners[i].y}px`;
                }
            });
        };

        root.appendChild(perspectiveWrapper);

        this.root = root;
        this.dom = root;
        this.css3dObject = new CSS3DObject(root);
        const initialScale = (this._css3dScale = this._getViewportScale()) * (this._userScale ?? 1);
        this.css3dObject.scale.set(initialScale, initialScale, 1);
        this._syncCornerHandleInverseScale();

        const geo = new BoxGeometry(0.1, 0.1, 0.1);
        const mat = new MeshBasicMaterial({ visible: false });
        this.anchor = new Mesh(geo, mat);
        this.anchor.name = 'inputFieldGuess';
        this.anchor.type = 'inputFieldGuess';
        this.anchor.groupDetails = {
            title: (typeof $trans !== 'undefined' && $trans('input-field-guess')) || 'Input guess',
            icon: 'quiz',
            editTitle: (typeof $trans !== 'undefined' && $trans('edit-input-field-guess')) || 'Edit input guess',
            dataLang: 'edit-input-field-guess',
        };
        this.anchor.add(this.css3dObject);

        this.mesh = this.anchor;
        this.object = this.anchor;
    }

    _getEventSourceId() {
        return this.dbConfig?.id ?? this.getId?.() ?? this.id;
    }

    _fireEvent(eventType) {
        if (!eventType || this.model?.editMode) return;
        const sourceId = this._getEventSourceId();
        if (sourceId == null) return;
        this.model?.view?._executeEventActions?.(sourceId, eventType);
    }

    showEditModal = () => {
        $(".input-field-guess-modal .modal-header-title").text($trans('edit-input-field-guess'));
        $(".input-field-guess-modal .modal-header-title").attr('data-lang', 'edit-input-field-guess');
        $(".input-field-guess-modal").addClass("edit");

        $("#input-field-guess-title").val(this._getTitle());
        $("#input-field-guess-question").val(this._getTitle());
        $("#input-field-guess-body").val(this._getBodyText());
        $("#input-field-guess-correct-answer").val('');
        $("#input-field-guess-correct-answer").attr('placeholder', $trans('input-field-guess-answer-edit-placeholder') || 'Leave blank to keep current answer');

        $("#input-field-guess-modal").addClass("is-active");
    };

    _clearCountdown = () => {
        if (this._countdownIntervalId) {
            window.clearInterval(this._countdownIntervalId);
            this._countdownIntervalId = null;
        }
        this._remainingCountdownSeconds = 0;
        $("#inputFieldGuessCountdown").text('').hide();
    };

    _renderCountdown = () => {
        if (this._remainingCountdownSeconds > 0) {
            $("#inputFieldGuessCountdown").text(formatCountdown(this._remainingCountdownSeconds)).show();
            return;
        }
        $("#inputFieldGuessCountdown").text('').hide();
    };

    _startCountdown = () => {
        this._clearCountdown();
        const seconds = this._getCountdownSeconds();
        if (seconds <= 0 || this.model?.editMode) return;

        this._remainingCountdownSeconds = seconds;
        this._renderCountdown();
        this._countdownIntervalId = window.setInterval(() => {
            if (!this._isModalOpen) {
                this._clearCountdown();
                return;
            }
            this._remainingCountdownSeconds -= 1;
            if (this._remainingCountdownSeconds <= 0) {
                this._clearCountdown();
                this.closeModal();
                return;
            }
            this._renderCountdown();
        }, 1000);
    };

    openModal = () => {
        if (this._isModalOpen || this.model?.editMode) return;
        this._isModalOpen = true;
        if (this.model?.view) {
            this.model.view._activeInputFieldGuess = this;
        }

        const title = this._getTitle() || $trans('input-field-guess');
        const body = this._getBodyText();
        $("#inputFieldGuessTitle").text(title);
        $("#inputFieldGuessBody").html(body ? escapeHtml(body).replace(/\n/g, '<br>') : '');
        $("#inputFieldGuessBody").toggle(!!body);
        $("#inputFieldGuessAnswer").val('');
        $("#inputFieldGuessModal").addClass("is-active");
        this._startCountdown();

        this._fireEvent('Open');
    };

    closeModal = () => {
        if (!this._isModalOpen) return;
        this._isModalOpen = false;
        if (this.model?.view?._activeInputFieldGuess === this) {
            this.model.view._activeInputFieldGuess = null;
        }

        this._clearCountdown();
        $("#inputFieldGuessAnswer").val('');
        $("#inputFieldGuessModal").removeClass("is-active");

        this._fireEvent('Close');
    };

    submitAnswer = () => {
        if (this._isSubmitting || this.model?.editMode) return;

        const answer = ($("#inputFieldGuessAnswer").val() || '').trim();
        if (!answer) {
            this.model?.alert?.('error', $trans('error-provide-answer'));
            return;
        }

        const worldId = this.model?.config?.worldId ?? this.model?.worldID;
        const objectId = this._getEventSourceId();
        const fetcher = this.model?.worldFetcher;

        if (!worldId || !objectId || !fetcher?.checkInputFieldGuessAnswer) {
            this.model?.alert?.('error', $trans('error-check-answer-failed'));
            return;
        }

        this._isSubmitting = true;
        fetcher.checkInputFieldGuessAnswer(
            worldId,
            objectId,
            answer,
            (res) => {
                this._isSubmitting = false;
                const correct = !!res?.correct;
                if (correct) {
                    this._fireEvent('USER_ANSWERED_TRUE');
                    if (this.dbConfig?.autocloseOnTrue) {
                        this.closeModal();
                    }
                } else {
                    this._fireEvent('USER_ANSWERED_FALSE');
                    if (this.dbConfig?.autocloseOnFalse) {
                        this.closeModal();
                    }
                }
            },
            () => {
                this._isSubmitting = false;
                this.model?.alert?.('error', $trans('error-check-answer-failed'));
            }
        );
    };

    _perspectiveOffsetsForDb() {
        const ew = this.root?.offsetWidth || this._domW || 1;
        const eh = this.root?.offsetHeight || this._domH || 1;
        return (this._perspectiveCorners || []).map((p) => ({
            x: p.x,
            y: p.y,
            xr: p.x / ew,
            yr: p.y / eh,
        }));
    }

    _getViewportScale() {
        const vpEl = this._viewportEl;
        const canvasH = vpEl?.clientHeight || 0;
        const winH = window.innerHeight;
        return (canvasH > 0 && winH > canvasH) ? winH / canvasH : 1;
    }

    _syncCornerHandleInverseScale() {
        if (!this.css3dObject) return;
        const s = Math.max(this.css3dObject.scale.x || 1, 0.02);
        const inv = 1 / s;
        const t = `translate(-50%,-50%) scale(${inv})`;
        for (const hnd of this._cornerHandles) {
            hnd.style.transform = t;
        }
    }

    _applyVisibilityFromConfig() {
        const inEditMode = !!this.model?.editMode || this.state.mode === 'edit';
        if (inEditMode) {
            this.anchor.visible = true;
            if (this.dom?.style) {
                this.dom.style.display = '';
                this.dom.classList.add('visible');
                this.dom.classList.remove('hide-on-start');
            }
            return;
        }
        this._detachViewVisuals();
    }

    _syncDomVisibilityToAnchor() {
        this._applyVisibilityFromConfig();
    }

    _clientToNDC(clientX, clientY) {
        const el = this._viewportEl;
        if (!el) {
            this._ndc.x = (clientX / this._sizes.width) * 2 - 1;
            this._ndc.y = -(clientY / this._sizes.height) * 2 + 1;
            return;
        }
        const rect = el.getBoundingClientRect();
        this._ndc.x = ((clientX - rect.left) / rect.width) * 2 - 1;
        this._ndc.y = 1 - ((clientY - rect.top) / rect.height) * 2;
    }

    _setFromClientXY(clientX, clientY) {
        this._clientToNDC(clientX, clientY);
        this._raycaster.setFromCamera(this._ndc, this.camera);
        if (this._raycaster.ray.intersectPlane(this._dragPlane, this._planeIntersectPoint)) {
            this._tmpVec.copy(this._planeIntersectPoint).add(this._dragGrabOffset);
            this._tmpVec.sub(this.camera.position).normalize();
            this._tmpVec.multiplyScalar(this._dragStartDistance).add(this.camera.position);
            if (this.anchor.parent) {
                this.anchor.parent.updateMatrixWorld(true);
                this.anchor.parent.worldToLocal(this._tmpVec);
                this.anchor.position.copy(this._tmpVec);
            } else {
                this.anchor.position.copy(this._tmpVec);
            }
        }
    }

    _onPointerMove = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!this.isDragging) return;
        this._didDragDuringPointer = true;
        this._setFromClientXY(e.clientX, e.clientY);
        this.anchor.lookAt(this.camera.position);
    };

    _onPointerUp = (e) => {
        if (!this.isDragging) return;
        this.isDragging = false;
        if (this._perspectiveContent) this._perspectiveContent.style.cursor = 'grab';
        if (this._captureEl) {
            try { this._captureEl.releasePointerCapture(e.pointerId); } catch (err) { }
            this._captureEl = null;
        }
        window.removeEventListener('pointermove', this._onPointerMove);
        window.removeEventListener('pointerup', this._onPointerUp);
        window.removeEventListener('pointercancel', this._onPointerUp);
        if (this.controls) this.controls.enabled = true;
        if (this.model?.view) {
            if (this.model.view.updateJSON) this.model.view.updateJSON(this.anchor, null);
            this.model.view.elementSelected = this;
            this.showObjectMenu();
            if (typeof $ !== 'undefined') $('.edit_element').trigger('click');
        }
    };

    _onPointerDown = (e) => {
        if (this.state.mode !== 'edit') return;
        if (this.dbConfig?.lockedInEdit) return;
        if (this.isDragging) return;
        if (e.target?.closest?.('.vr-input-field-guess-corner-handle')) return;
        this.isDragging = true;
        this._captureEl = e.currentTarget || this.dom;
        try { this._captureEl?.setPointerCapture?.(e.pointerId); } catch (err) { }
        if (this.anchor.parent) {
            this.anchor.parent.updateMatrixWorld(true);
            this.anchor.getWorldPosition(this._tmpVec);
        } else {
            this._tmpVec.copy(this.anchor.position);
        }
        this._dragPlaneNormal.subVectors(this._tmpVec, this.camera.position).normalize();
        this._dragPlane.setFromNormalAndCoplanarPoint(this._dragPlaneNormal, this._tmpVec);
        this._dragStartDistance = this.camera.position.distanceTo(this._tmpVec);
        this._clientToNDC(e.clientX, e.clientY);
        this._raycaster.setFromCamera(this._ndc, this.camera);
        if (this._raycaster.ray.intersectPlane(this._dragPlane, this._planeIntersectPoint)) {
            this._dragGrabOffset.copy(this._tmpVec).sub(this._planeIntersectPoint);
        } else {
            this._dragGrabOffset.set(0, 0, 0);
        }
        if (this._perspectiveContent) this._perspectiveContent.style.cursor = 'grabbing';
        window.addEventListener('pointermove', this._onPointerMove);
        window.addEventListener('pointerup', this._onPointerUp);
        window.addEventListener('pointercancel', this._onPointerUp);
        if (this.controls) this.controls.enabled = false;
    };

    _attachDragListeners() {
        if (this._perspectiveContent) this._perspectiveContent.style.cursor = 'grab';
    }

    _detachDragListeners() {
        if (this._perspectiveContent) this._perspectiveContent.style.cursor = '';
        window.removeEventListener('pointermove', this._onPointerMove);
        window.removeEventListener('pointerup', this._onPointerUp);
        window.removeEventListener('pointercancel', this._onPointerUp);
        this.isDragging = false;
        if (this.controls) this.controls.enabled = true;
    }

    render(camera) {
        if (!camera || !this._isEditMode) return;
        if (!this.isDragging) {
            this.anchor.lookAt(camera.position);
            this._css3dScale = this._getViewportScale();
            const scale = this._css3dScale * (this._userScale ?? 1);
            if (this.css3dObject) this.css3dObject.scale.set(scale, scale, 1);
        }
        this._syncCornerHandleInverseScale();
    }

    getId() {
        return this.id;
    }

    setId(id) {
        this.id = id;
    }

    getDbConfig() {
        return this.dbConfig;
    }

    setDbConfig(config) {
        this.dbConfig = { ...this.dbConfig, ...config };
        if (config?.title !== undefined || config?.question !== undefined || config?.bodyText !== undefined) {
            this._updatePosterContent();
        }
        if (config?.timePreset !== undefined) {
            this.dbConfig.timePreset = config.timePreset ? normalizeTimePreset(config.timePreset) : '';
            if (this._isModalOpen) this._startCountdown();
        }
        if (config?.size !== undefined || config?.scale !== undefined) {
            const s = config.size ?? config.scale;
            this._userScale = Number(s) > 0 ? Number(s) : 1;
        }
        if (config?.perspectiveOffsets && Array.isArray(config.perspectiveOffsets) && this._perspectiveCorners) {
            const w = this.root?.offsetWidth || this._domW || 1;
            const h = this.root?.offsetHeight || this._domH || 1;
            this._perspectiveCorners.splice(0, 4, ...config.perspectiveOffsets.map((p) => (p.xr !== undefined && p.yr !== undefined)
                ? { x: Number(p.xr) * w, y: Number(p.yr) * h }
                : { x: Number(p.x) ?? p.x, y: Number(p.y) ?? p.y }));
            this._syncHandlePositions?.();
            this._applyPerspectiveTransform?.();
        }
        this._applyVisibilityFromConfig();
    }

    getLimits() {
        return { min: 280, max: 800, distance: Number(this.dbConfig?.distance) || 540 };
    }

    getAxisRotations() {
        return { rotateX: 0, rotateY: 0 };
    }

    setDistance(val) {
        const n = Number(val);
        if (!Number.isFinite(n) || n <= 0 || !this.anchor?.position?.setLength) return;
        const { min, max } = this.getLimits();
        const clamped = Math.min(max, Math.max(min, n));
        this.anchor.position.setLength(clamped);
        this.dbConfig = { ...this.dbConfig, distance: clamped };
    }

    setSize(val) {
        const n = Number(val);
        if (!Number.isFinite(n) || n <= 0) return;
        this._userScale = n;
        this.dbConfig = { ...this.dbConfig, size: n };
    }

    setAxisRotations() { }

    showObjectMenu() {
        const title = (typeof $trans !== 'undefined' && $trans('input-field-guess')) || 'Input guess';
        $('.object-menu .object-name').text(title);
        $('.object-menu').addClass('is-active');
        $('.object-menu').addClass('edit');
        $('.input-field-guess-settings').removeClass('hidden').addClass('is-active');
        $('.vrai-agent-settings, .tooltip-settings, .video-loop, .video-transparent, .iframe-agent-settings, .timer-element-settings').addClass('hidden');
        $('.perspective-settings').removeClass('hidden');
        $(".object-menu .flx-sb:has(#show_edit_modal)").removeClass('hidden');
    }

    toggleSelect() {
        this.dom?.querySelector('.vr-input-field-guess-poster')?.classList?.toggle('active');
    }

    resetPerspective() {
        if (!this._perspectiveCorners || !this._originalPos) return;
        this._perspectiveCorners.splice(0, 4, ...this._originalPos.map((p) => ({ ...p })));
        this._syncHandlePositions?.();
        this._applyPerspectiveTransform?.();
        this.dbConfig = { ...this.dbConfig, perspectiveOffsets: this._perspectiveOffsetsForDb() };
    }

    destroy() {
        try { this._detachDragListeners(); } catch (e) { }
        this._clearCountdown();
        this._detachViewVisuals();
        if (this.dom?.parentNode) this.dom.parentNode.removeChild(this.dom);
        document.querySelectorAll(`[data-id='input_field_guess_${this.id}']`).forEach((node) => node?.remove?.());
    }
}

ComponentRegistry.register({
    type: 'inputFieldGuess',
    displayNameKey: 'input-field-guess',
    getDisplayName: (obj) => (obj?.title || obj?.name || obj?.dbConfig?.title || obj?.dbConfig?.name || (typeof $trans !== 'undefined' && $trans('input-field-guess')) || 'Input guess'),
    events: [
        { value: 'Open', labelKey: 'event-input-guess-open' },
        { value: 'Close', labelKey: 'event-input-guess-close' },
        { value: 'USER_ANSWERED_TRUE', labelKey: 'event-user-answered-true' },
        { value: 'USER_ANSWERED_FALSE', labelKey: 'event-user-answered-false' },
        { value: 'click', labelKey: 'event-click' },
        { value: 'raycastEnter', labelKey: 'event-raycast-enter' },
        { value: 'raycastLeave', labelKey: 'event-raycast-leave' },
    ],
    actions: [
        { value: 'open', labelKey: 'action-input-guess-open' },
        { value: 'close', labelKey: 'action-input-guess-close' },
        { value: 'closeElement', labelKey: 'action-close-element' },
    ],
});

export { VRInputFieldGuess };
